import { useEffect, useState } from 'react';
import {
  Controller,
  UseFormReturn,
  useFieldArray,
  useWatch,
} from 'react-hook-form';

import { AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, Plus } from 'lucide-react';

import { Button } from 'components/Button';
import { Checkbox } from 'components/Checkbox';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'components/Collapsible';
import { DatePicker } from 'components/DatePicker';
import { Label } from 'components/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/Select';
import { RHFTextInput } from 'components/input/RHFTextInput';
import { Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography';

import {
  DIRECT_INDIRECT_OPTIONS,
  LTLAccessorial,
  NEEDS_REVIEW_OPTIONS_THREEG,
  QuickQuoteInputs,
  RATED_INTERMODAL_SERVICE_OPTIONS,
  RATED_LTL_SERVICE_OPTIONS,
  RATED_PARCEL_SERVICE_OPTIONS,
  ThreeGQuoteCharge,
} from '../../types';
import { QuoteChargeCard } from './QuoteChargeCard';

interface RateInformationSectionProps {
  formMethods: UseFormReturn<QuickQuoteInputs, any, QuickQuoteInputs>;
  accessorials: LTLAccessorial[];
}

export function RateInformationSection({
  formMethods,
  accessorials,
}: RateInformationSectionProps) {
  const { control, setValue } = formMethods;
  const [isRatedServiceOpen, setIsRatedServiceOpen] = useState(false);
  const ltl = useWatch({ control, name: 'ltl' });
  const rateInfo = useWatch({ control, name: 'ltl.rateInfo' });
  const quoteCharges = useWatch({ control, name: 'ltl.quoteCharges' });
  const ratedServiceInfo = useWatch({ control, name: 'ltl.ratedServiceInfo' });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'ltl.quoteCharges',
  });

  useEffect(() => {
    if (!ltl) {
      return;
    }

    if (rateInfo === undefined) {
      setValue('ltl.rateInfo', { doNotOverwrite: false });
    }

    if (!Array.isArray(quoteCharges)) {
      setValue('ltl.quoteCharges', []);
    }

    if (ratedServiceInfo === undefined) {
      setValue('ltl.ratedServiceInfo', {});
    }
  }, [ltl, quoteCharges, rateInfo, ratedServiceInfo, setValue]);

  const handleAddQuoteCharge = () => {
    const newCharge: ThreeGQuoteCharge = {
      chargeId: crypto.randomUUID(),
    };
    append(newCharge);
  };

  return (
    <Flex direction='col' gap='md' className='w-full mx-0'>
      <Typography
        variant='h5'
        className='text-neutral-800 font-semibold whitespace-nowrap'
      >
        Rate Information
      </Typography>

      <div className='mb-1'>
        <Controller
          name='ltl.rateInfo.doNotOverwrite'
          control={control}
          render={({ field }) => (
            <Checkbox
              label='Do Not Overwrite'
              labelClassName='leading-none text-neutral-600 peer-disabled:cursor-not-allowed peer-disabled:opacity-70'
              checked={field.value ?? false}
              onCheckedChange={(checked) => field.onChange(checked)}
            />
          )}
        />
      </div>

      <div className='w-full'>
        <Label name='ltl.rateInfo.needsReview'>Needs Review</Label>
        <Controller
          name='ltl.rateInfo.needsReview'
          control={control}
          render={({ field }) => (
            <Select onValueChange={field.onChange} value={field.value ?? ''}>
              <SelectTrigger className='w-full mt-1'>
                <SelectValue placeholder='Select' />
              </SelectTrigger>
              <SelectContent>
                {NEEDS_REVIEW_OPTIONS_THREEG.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      </div>

      <Flex align='center' justify='between' className='w-full'>
        <Typography variant='body' weight='medium'>
          Quote Charges
        </Typography>
        <Button
          type='button'
          variant='outline'
          size='sm'
          onClick={handleAddQuoteCharge}
          className='h-8 px-3 text-xs'
          buttonNamePosthog={null}
        >
          <Plus className='h-4 w-4 mr-1' />
          Add Charge
        </Button>
      </Flex>

      {fields.length > 0 && (
        <AnimatePresence>
          <Flex direction='col' gap='md' className='w-full'>
            {fields.map((field, index) => (
              <QuoteChargeCard
                key={field.id}
                index={index}
                formMethods={formMethods}
                onRemove={() => remove(index)}
                accessorials={accessorials}
              />
            ))}
          </Flex>
        </AnimatePresence>
      )}

      <Collapsible
        open={isRatedServiceOpen}
        onOpenChange={setIsRatedServiceOpen}
        className='w-full'
      >
        <CollapsibleTrigger
          type='button'
          className='w-full cursor-pointer py-2'
        >
          <Flex align='center' justify='between' className='w-full'>
            <Typography
              variant='h5'
              className='text-neutral-800 font-semibold whitespace-nowrap'
            >
              Rated Service Information
            </Typography>
            {isRatedServiceOpen ? (
              <ChevronUp className='h-4 w-4 text-neutral-500' />
            ) : (
              <ChevronDown className='h-4 w-4 text-neutral-500' />
            )}
          </Flex>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className='pt-2'>
            <Grid cols='2' gap='lg' align='stretch' className='w-full mx-0'>
              <div className='w-full flex flex-col justify-end'>
                <Label
                  name='ltl.ratedServiceInfo.ratedParcelService'
                  className='mb-1'
                >
                  Rated Parcel Service
                </Label>
                <Controller
                  name='ltl.ratedServiceInfo.ratedParcelService'
                  control={control}
                  render={({ field }) => (
                    <Select
                      onValueChange={field.onChange}
                      value={field.value ?? ''}
                    >
                      <SelectTrigger className='w-full'>
                        <SelectValue placeholder='Select' />
                      </SelectTrigger>
                      <SelectContent>
                        {RATED_PARCEL_SERVICE_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>

              <div className='w-full flex flex-col justify-end'>
                <Label
                  name='ltl.ratedServiceInfo.ratedLtlService'
                  className='mb-1'
                >
                  Rated LTL Service
                </Label>
                <Controller
                  name='ltl.ratedServiceInfo.ratedLtlService'
                  control={control}
                  render={({ field }) => (
                    <Select
                      onValueChange={field.onChange}
                      value={field.value ?? ''}
                    >
                      <SelectTrigger className='w-full'>
                        <SelectValue placeholder='Select' />
                      </SelectTrigger>
                      <SelectContent>
                        {RATED_LTL_SERVICE_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>

              <div className='w-full flex flex-col justify-end'>
                <Label
                  name='ltl.ratedServiceInfo.ratedIntermodalService'
                  className='whitespace-normal leading-tight mb-1'
                >
                  Rated Intermodal Service
                </Label>
                <Controller
                  name='ltl.ratedServiceInfo.ratedIntermodalService'
                  control={control}
                  render={({ field }) => (
                    <Select
                      onValueChange={field.onChange}
                      value={field.value ?? ''}
                    >
                      <SelectTrigger className='w-full'>
                        <SelectValue placeholder='Select' />
                      </SelectTrigger>
                      <SelectContent>
                        {RATED_INTERMODAL_SERVICE_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>

              <div className='w-full flex flex-col justify-end'>
                <Label name='ltl.ratedServiceInfo.estDelivery' className='mb-1'>
                  Est Delivery
                </Label>
                <Controller
                  name='ltl.ratedServiceInfo.estDelivery'
                  control={control}
                  render={({ field }) => <DatePicker field={field} />}
                />
              </div>

              <div className='w-full flex flex-col justify-end'>
                <RHFTextInput
                  name='ltl.ratedServiceInfo.estTransitDays'
                  label='Est. Transit Days'
                  inputType='number'
                  placeholder='0'
                  options={{ valueAsNumber: true, min: 0 }}
                  required={false}
                />
              </div>

              <div className='w-full flex flex-col justify-end'>
                <Label
                  name='ltl.ratedServiceInfo.directIndirect'
                  className='mb-1'
                >
                  Direct/Indirect
                </Label>
                <Controller
                  name='ltl.ratedServiceInfo.directIndirect'
                  control={control}
                  render={({ field }) => (
                    <Select
                      onValueChange={field.onChange}
                      value={field.value ?? ''}
                    >
                      <SelectTrigger className='w-full'>
                        <SelectValue placeholder='Select' />
                      </SelectTrigger>
                      <SelectContent>
                        {DIRECT_INDIRECT_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>

              <div className='w-full flex flex-col justify-end'>
                <RHFTextInput
                  name='ltl.ratedServiceInfo.quoteRefNum'
                  label='Quote Ref Num'
                  required={false}
                />
              </div>

              <div className='w-full flex flex-col justify-end'>
                <Label
                  name='ltl.ratedServiceInfo.effectiveDate'
                  className='mb-1'
                >
                  Effective Date
                </Label>
                <Controller
                  name='ltl.ratedServiceInfo.effectiveDate'
                  control={control}
                  render={({ field }) => <DatePicker field={field} />}
                />
              </div>

              <div className='w-full flex flex-col justify-end'>
                <Label
                  name='ltl.ratedServiceInfo.expirationDate'
                  className='mb-1'
                >
                  Expiration Date
                </Label>
                <Controller
                  name='ltl.ratedServiceInfo.expirationDate'
                  control={control}
                  render={({ field }) => <DatePicker field={field} />}
                />
              </div>
            </Grid>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Flex>
  );
}
